import {
  Popover as PopoverRoot,
  PopoverProps as PrimitivePopoverProps,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives";
import React from "react";
import { cn } from "@gettactic/helpers";
import { ScrollArea } from "@v2/components/ui/scroll-area";

export interface PopoverProps extends PrimitivePopoverProps {
  content: React.ReactNode;
  footer?: React.ReactNode;
  sideContent?: React.ReactNode;
  className?: string;
  triggerClassname?: string;
  contentProps?: React.ComponentProps<typeof PopoverContent>;
  bodyProps?: React.ComponentProps<"div">;
  triggerAsChild?: boolean;
  mainContainerClassName?: string;
}

const classes = {
  Trigger: "w-full select-none",
  Content:
    "flex flex-1 border-1 border-neutral-100 rounded-[10px] p-0 overflow-hidden",
  Body: "p-4",
  Footer:
    "select-none flex items-center border-t-1 border-t-neutral-100 h-[44px]",
  SideContent:
    "flex flex-1 border-l-1 border-l-neutral-100 h-full bg-neutral py-6 pl-6 pr-5"
};

export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  footer,
  sideContent,
  onOpenChange,
  contentProps,
  bodyProps,
  triggerAsChild = false,
  mainContainerClassName = "w-full",
  ...props
}) => {
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  const [mainContentHeight, setMainContentHeight] = React.useState(0);
  const [isOpen, setOpen] = React.useState(false);
  const hasSideContent = !!sideContent;

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    onOpenChange?.(open);
  };

  React.useEffect(() => {
    if (hasSideContent && mainContentRef.current && isOpen) {
      setMainContentHeight(mainContentRef.current.clientHeight);
    }
  }, [mainContentRef.current, isOpen, hasSideContent]);

  return (
    <PopoverRoot {...props} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        asChild={triggerAsChild}
        className={cn(classes.Trigger, props.triggerClassname)}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        showArrow={false}
        {...contentProps}
        className={cn(
          classes.Content,
          props.className,
          contentProps?.className
        )}
        style={{
          boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.1)",
          ...contentProps?.style
        }}
      >
        {(isOpen || props.open === true) && (
          <div className={"flex w-full items-stretch"}>
            <div ref={mainContentRef} className={mainContainerClassName}>
              <div
                {...bodyProps}
                className={cn(classes.Body, bodyProps?.className)}
              >
                {content}
              </div>
              {footer && <div className={classes.Footer}>{footer}</div>}
            </div>
            {hasSideContent && (
              <ScrollArea
                className={classes.SideContent}
                style={
                  mainContentHeight ? { height: mainContentHeight } : undefined
                }
              >
                {sideContent}
              </ScrollArea>
            )}
          </div>
        )}
      </PopoverContent>
    </PopoverRoot>
  );
};
